import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  type MultiFactorResolver,
  type RecaptchaVerifier,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  beginMfaResolver,
  buildMfaAssertion,
  createInvisibleRecaptcha,
  isMultiFactorAuthRequired,
  MfaRequiredError,
  sendPhoneMfaVerification as sendPhoneMfaVerificationCode,
  summarizeMfaHints,
  type MfaPendingState,
} from './mfa-sign-in';
import { auth, googleProvider } from '../firebase';
import { useLocaleStore } from '@/stores/locale-store';
import { normalizeLocale } from '@/i18n/supported-languages';
import { normalizePrimaryColor, themeFromApiPreference, type PrimaryColor, useThemeStore } from '@/stores/theme-store';
import { isElectron } from '@/utils/platform';
import api, {
  AUTH_ERROR_MESSAGE_KEY,
  AUTH_EXPIRED_EVENT,
  EMAIL_NOT_VERIFIED_EVENT,
  EMAIL_VERIFICATION_MESSAGE_KEY,
  REDIRECT_VERIFY_EMAIL_KEY,
  WORKSPACE_ACCESS_DENIED_EVENT,
  clearClientActiveAid,
  getClientActiveAid,
  getMessageFromResponse,
  hydrateClientActiveAidFromSw,
  isAxiosUnauthorizedError,
  isEmailNotVerifiedMessage,
  isEmailNotVerifiedResponse,
  isTransientAuthFailure,
  subscribeActiveAidChanges,
} from '../api';
import {
  cancelMfaSignInViaSw,
  completeMfaSignInViaSw,
  getSwAuthUser,
  isSwMfaRequiredError,
  sendMfaPhoneVerificationViaSw,
  signInWithEmailAndPasswordViaSw,
  signInWithGoogleViaSw,
  signUpViaSw,
  updateProfileViaSw,
} from '../extension/chromeAuthBridge';
import { saveExtensionReturnPath } from '../extension/extensionReturnPath';
import { setErrorTrackingUser } from '@/errorTracking';
import { useSessionsStore } from '@/stores/sessions-store';
import {
  reloadSessionsAfterAccountSwitch,
} from '@/hooks/useLoadSessions';
import {
  seedClientActiveAidFromPreference,
  shouldSkipAccountSwitchBroadcastReload,
} from '@/utils/notifyAccountSwitch';
import { buildPasswordResetActionCodeSettings } from '@/auth/password-reset';
import type { SessionContextGlobalConfig } from '@/types/session-context';

/**
 * The backend returns HTTP 200 with { status: 'error', message: '...' } for
 * account-level rejections (e.g. unverified email). Convert these into a
 * thrown error so every callsite can handle them uniformly.
 */
class PrefAppError extends Error {
  readonly isAppError = true as const;
  constructor(message: string) {
    super(message);
    this.name = 'PrefAppError';
  }
}

function throwIfPrefAppError(data: Record<string, unknown>): void {
  if (data?.status === 'error' || data?.status === 'fail') {
    throw new PrefAppError((data.message as string) || 'Account error.');
  }
}

export type UserPreferences = {
  troveAutoSave: boolean;
  highlighter: boolean;
  /** Floating bookmark/sticky launcher on web pages (extension); default true when omitted */
  pageToolsFab?: boolean;
  theme: 'dark' | 'light' | 'auto';
  /** UI language; persisted via GET/PUT /preference */
  locale?: string;
  /** Primary accent color preset key; persisted via GET/PUT /preference */
  primaryColor?: PrimaryColor;
  /** Whether the user has seen the onboarding tour; persisted via GET/PUT /preference */
  hasSeenOnboarding?: boolean;
  /** Server applies AI bookmark context after save (no Quick context modal). Default false. */
  autoBookmarkContext?: boolean;
  /** GET /parseUrl may return aiPlacement for group/tag suggestions. Default false. */
  aiAutoSuggestGroupTags?: boolean;
  /** Smart session context (Session Context Guard) global toggles. */
  sessionContextGlobal?: SessionContextGlobalConfig;
};

type PreferenceApiPayload = {
  aid?: string;
  defaultAid?: string;
  auid?: string;
  accountRole?: 'admin' | 'user';
  isAccountAdmin?: boolean;
  isAccountOwner?: boolean;
  preference?: UserPreferences;
  status?: string;
  message?: string;
};

function workspaceFieldsFromPreference(prefData: PreferenceApiPayload) {
  const localAid = getClientActiveAid() ?? prefData.aid;
  return {
    aid: localAid,
    defaultAid: prefData.defaultAid ?? prefData.aid,
    preference: prefData.preference,
    auid: prefData.auid,
    accountRole: prefData.accountRole,
    isAccountAdmin: prefData.isAccountAdmin,
    isAccountOwner: prefData.isAccountOwner,
  };
}

/** Defaults when GET /preference fails for non-auth reasons (timeout, 5xx, app-level body error). */
const FALLBACK_PREFERENCES: UserPreferences = {
  troveAutoSave: false,
  highlighter: true,
  pageToolsFab: true,
  theme: 'auto',
  locale: 'en',
  primaryColor: 'blue',
  hasSeenOnboarding: false,
  autoBookmarkContext: false,
  aiAutoSuggestGroupTags: false,
};

export type User = {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  aid?: string;
  /** Server default workspace for new devices. */
  defaultAid?: string;
  /** Account owner uid (`accounts/{aid}.adminId`). */
  auid?: string;
  accountRole?: 'admin' | 'user';
  isAccountAdmin?: boolean;
  isAccountOwner?: boolean;
  preference?: UserPreferences;
};

type ExtensionUser = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
};

function userFromExtensionWithFallback(swUser: ExtensionUser): User {
  return {
    id: swUser.uid,
    email: swUser.email || '',
    name: swUser.displayName || swUser.email?.split('@')[0] || 'User',
    avatar: swUser.photoURL || undefined,
    aid: undefined,
    preference: { ...FALLBACK_PREFERENCES },
  };
}

function userFromFirebaseWithFallback(fu: {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}): User {
  return {
    id: fu.uid,
    email: fu.email || '',
    name: fu.displayName || fu.email?.split('@')[0] || 'User',
    avatar: fu.photoURL || undefined,
    aid: undefined,
    preference: { ...FALLBACK_PREFERENCES },
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** Set when password sign-in needs a TOTP / SMS code before completing. */
  mfaPending: MfaPendingState | null;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, pass: string) => Promise<void>;
  signupWithEmail: (email: string, pass: string, name: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  cancelMfaSignIn: () => void;
  completeMfaSignIn: (opts: {
    code: string;
    hintIndex?: number;
    phoneVerificationId?: string;
  }) => Promise<void>;
  sendMfaPhoneVerification: (hintIndex?: number) => Promise<string>;
  /** Persists partial preferences via PUT /preference and updates local user state. */
  updatePreference: (patch: Partial<UserPreferences>) => Promise<void>;
  /** Updates in-memory preference fields without an API call (after server-side sync). */
  patchLocalPreference: (patch: Partial<UserPreferences>) => void;
  /** Persists display name to Firebase Auth and updates local user state. */
  updateDisplayName: (name: string) => Promise<void>;
  /** Refetch GET /preference and merge aid + prefs into user (workspace switch). */
  refreshUserPreference: () => Promise<void>;
}

export { MfaRequiredError };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState<MfaPendingState | null>(null);
  const extensionAuthSeqRef = useRef(0);
  const applyExtensionUserFromSwRef = useRef<((swUser: ExtensionUser | null) => Promise<void>) | null>(
    null
  );
  const mfaResolverRef = useRef<MultiFactorResolver | null>(null);
  const mfaRecaptchaRef = useRef<RecaptchaVerifier | null>(null);
  /** Extension: MFA resolver lives in the service worker, not this page. */
  const mfaViaServiceWorkerRef = useRef(false);

  useEffect(() => {
    setErrorTrackingUser(
      user ? { id: user.id, email: user.email ?? undefined } : null,
    );
  }, [user]);

  const clearMfaSignIn = useCallback(() => {
    if (mfaViaServiceWorkerRef.current) {
      void cancelMfaSignInViaSw();
    }
    mfaViaServiceWorkerRef.current = false;
    mfaResolverRef.current = null;
    if (mfaRecaptchaRef.current) {
      try {
        mfaRecaptchaRef.current.clear();
      } catch {
        // ignore
      }
      mfaRecaptchaRef.current = null;
    }
    setMfaPending(null);
  }, []);

  const startMfaSignIn = useCallback((error: unknown, email: string) => {
    const resolver = beginMfaResolver(auth, error);
    mfaResolverRef.current = resolver;
    mfaViaServiceWorkerRef.current = false;
    setMfaPending({ email, hints: summarizeMfaHints(resolver) });
  }, []);

  const startMfaSignInFromSw = useCallback((payload: MfaPendingState) => {
    mfaViaServiceWorkerRef.current = true;
    mfaResolverRef.current = null;
    setMfaPending(payload);
  }, []);

  /** Load Trove user state after Firebase Auth sign-in (password, Google, or MFA). */
  const finalizeFirebaseSignIn = useCallback(async (firebaseUser: FirebaseUser) => {
    try {
      const prefResponse = await api.get('/preference');
      const prefData = prefResponse.data as PreferenceApiPayload;
      throwIfPrefAppError(prefData as Record<string, unknown>);
      seedClientActiveAidFromPreference(prefData);
      setUser({
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        avatar: firebaseUser.photoURL || undefined,
        ...workspaceFieldsFromPreference(prefData),
      });
    } catch (prefError) {
      if (prefError instanceof PrefAppError) {
        if (isEmailNotVerifiedMessage(prefError.message)) {
          try {
            sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, prefError.message);
            sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
          } catch {
            // ignore
          }
          setUser(null);
          throw prefError;
        }
        setUser(userFromFirebaseWithFallback(firebaseUser));
        return;
      }
      const axiosData = (prefError as { response?: { data?: unknown } })?.response?.data;
      if (isEmailNotVerifiedResponse(axiosData)) {
        const msg =
          typeof (axiosData as { message?: string }).message === 'string'
            ? (axiosData as { message: string }).message
            : 'Email id not verified. Please check your email and verify your email address.';
        try {
          sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, msg);
          sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
        } catch {
          // ignore
        }
        setUser(null);
        throw prefError;
      }
      if (isTransientAuthFailure(prefError)) {
        setUser(userFromFirebaseWithFallback(firebaseUser));
        return;
      }
      if (isAxiosUnauthorizedError(prefError)) {
        await signOut(auth);
        throw prefError;
      }
      setUser(userFromFirebaseWithFallback(firebaseUser));
    }
  }, []);

  /**
   * Service worker owns Firebase in chrome-extension:; load /preference after SW user is known.
   * Shared by AUTH_STATE_CHANGED, getUser on mount, and email login/signup via chromeAuthBridge.
   */
  const applyExtensionUserFromSw = useCallback(async (swUser: ExtensionUser | null) => {
    if (typeof window === 'undefined' || window.location.protocol !== 'chrome-extension:') {
      return;
    }
    const seq = ++extensionAuthSeqRef.current;
    const isStale = () => seq !== extensionAuthSeqRef.current;

    if (swUser) {
      try {
        if (typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:') {
          await hydrateClientActiveAidFromSw();
        }
        const prefResponse = await api.get('/preference');
        if (isStale()) return;
        const prefData = prefResponse.data as PreferenceApiPayload;
        throwIfPrefAppError(prefData as Record<string, unknown>);
        seedClientActiveAidFromPreference(prefData);
        console.log('applyUser -> setting user ', swUser);
        setUser({
          id: swUser.uid,
          email: swUser.email || '',
          name: swUser.displayName || swUser.email?.split('@')[0] || 'User',
          avatar: swUser.photoURL || undefined,
          ...workspaceFieldsFromPreference(prefData),
        });
      } catch (error) {
        if (error instanceof PrefAppError) {
          if (isEmailNotVerifiedMessage(error.message)) {
            try {
              sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, error.message);
              sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
            } catch {
              // ignore
            }
            if (isStale()) return;
            setUser(null);
            setLoading(false);
            return;
          }
          console.warn('[Auth] Preference app-level rejection in extension context (keeping session):', error.message);
          if (isStale()) return;
          setUser(userFromExtensionWithFallback(swUser));
          setLoading(false);
          return;
        }
        const axiosData = (error as { response?: { data?: unknown } })?.response?.data;
        if (isEmailNotVerifiedResponse(axiosData)) {
          const msg =
            typeof (axiosData as { message?: string }).message === 'string'
              ? (axiosData as { message: string }).message
              : 'Email id not verified. Please check your email and verify your email address.';
          try {
            sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, msg);
            sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
          } catch {
            // ignore
          }
          if (isStale()) return;
          setUser(null);
          setLoading(false);
          return;
        }
          if (isTransientAuthFailure(error)) {
            console.warn('[Auth] Transient token refresh failure in extension context — keeping session');
            if (isStale()) return;
            setUser(userFromExtensionWithFallback(swUser));
            setLoading(false);
            return;
          }
          if (isAxiosUnauthorizedError(error)) {
            const confirm = await getSwAuthUser();
            if (isStale()) return;
            if (confirm.user) {
              console.warn(
                '[Auth] Unauthorized loading preferences but SW still has a user — keeping session'
              );
              setUser(userFromExtensionWithFallback(confirm.user));
              setLoading(false);
              return;
            }
            if (!confirm.ok) {
              console.warn(
                '[Auth] Unauthorized loading preferences but SW unreachable — keeping session'
              );
              if (isStale()) return;
              setUser(userFromExtensionWithFallback(swUser));
              setLoading(false);
              return;
            }
            const apiMessage =
              getMessageFromResponse(axiosData) || 'Session expired. Please sign in again.';
            try {
              sessionStorage.setItem(AUTH_ERROR_MESSAGE_KEY, apiMessage);
            } catch {
              // ignore
            }
            console.error(
              '[Auth] Unauthorized loading preferences in extension context:',
              error
            );
            if (isStale()) return;
            setUser(null);
            setLoading(false);
            try {
              (
                window as unknown as {
                  chrome?: { runtime?: { sendMessage: (msg: { action: string }) => void } };
                }
              ).chrome?.runtime?.sendMessage({ action: 'POPUP_SIGN_OUT' });
            } catch {
              // ignore
            }
            saveExtensionReturnPath();
            if (window.location.hash !== '#/login') {
              window.location.hash = '#/login';
            }
            return;
          }
        console.warn('[Auth] Preference request failed (keeping session, using defaults):', error);
        if (isStale()) return;
        setUser(userFromExtensionWithFallback(swUser));
        setLoading(false);
      }
    } else {
      // A transient auth flap in the service worker can broadcast
      // AUTH_STATE_CHANGED(null) even though the persisted session is still
      // valid. Re-confirm with the SW (its `getUser` handler waits for
      // `authStateReady()`) before tearing the UI down, so the profile page
      // doesn't bounce login -> home on an MV3 cold-start blip.
      const confirm = await getSwAuthUser();
      if (isStale()) return;
      if (confirm.user) {
        void applyExtensionUserFromSwRef.current?.(confirm.user);
        return;
      }
      if (!confirm.ok) {
        // SW unreachable — don't sign out a possibly-valid session. Genuine
        // logout is user-initiated and flows through `logout()`, not here.
        setLoading(false);
        return;
      }
      setUser(null);
    }
    if (isStale()) return;
    setLoading(false);
  }, []);
  applyExtensionUserFromSwRef.current = applyExtensionUserFromSw;

  const refreshUserPreference = useCallback(async () => {
    const prefResponse = await api.get('/preference');
    const prefData = prefResponse.data as PreferenceApiPayload;
    throwIfPrefAppError(prefData);
    setUser((prev) =>
      prev
        ? {
            ...prev,
            ...workspaceFieldsFromPreference(prefData),
            preference: prefData.preference ?? prev.preference,
          }
        : prev
    );
    const pref = prefData.preference;
    if (pref?.locale) {
      useLocaleStore.getState().setLocale(normalizeLocale(pref.locale));
    }
    if (pref?.primaryColor) {
      useThemeStore.getState().setPrimaryColor(normalizePrimaryColor(pref.primaryColor));
    }
    if (pref?.theme) {
      useThemeStore.getState().setTheme(themeFromApiPreference(pref.theme));
    }
  }, []);

  /** Sync i18n + theme stores from profile; reset to defaults when logged out. */
  useEffect(() => {
    if (!user) {
      useLocaleStore.getState().setLocale('en');
      useThemeStore.getState().setPrimaryColor('blue');
      useThemeStore.getState().setTheme('system');
      return;
    }
    const lng = normalizeLocale(user.preference?.locale);
    useLocaleStore.getState().setLocale(lng);
    const apiPrimary = user.preference?.primaryColor;
    if (apiPrimary) {
      useThemeStore.getState().setPrimaryColor(normalizePrimaryColor(apiPrimary));
    }
    const apiTheme = user.preference?.theme;
    if (apiTheme) {
      useThemeStore.getState().setTheme(themeFromApiPreference(apiTheme));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.preference?.locale, user?.preference?.primaryColor, user?.preference?.theme]);

  useEffect(() => {
    const unsubscribe = subscribeActiveAidChanges((aid) => {
      if (!aid) return;
      setUser((prev) => (prev ? { ...prev, aid } : prev));
      useSessionsStore.getState().resetForAccountSwitch();
      void reloadSessionsAfterAccountSwitch().catch(() => undefined);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // When running inside a Chrome extension the service worker owns the Firebase
    // session. Get the current user from the SW and listen for AUTH_STATE_CHANGED
    // broadcasts instead of running a second Firebase listener.
    const isExtension = typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';

    const workspaceDeniedListener = (event: Event) => {
      const detail = (event as CustomEvent<{ defaultAid?: string; message?: string }>).detail;
      if (detail?.defaultAid) {
        setUser((prev) =>
          prev ? { ...prev, aid: detail.defaultAid, defaultAid: detail.defaultAid } : prev
        );
      }
      void refreshUserPreference().catch(() => undefined);
    };

    if (isExtension) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const chrome = (window as any).chrome;

      const clearExtensionAuthUi = () => {
        setUser(null);
        setLoading(false);
      };

      const redirectExtensionPopupToLogin = () => {
        saveExtensionReturnPath();
        if (window.location.hash !== '#/login') {
          window.location.hash = '#/login';
        }
      };

      // Fetch current user from service worker on mount
      console.log('[Auth] Extension context — fetching user from service worker...');
      chrome.runtime.sendMessage({ action: 'getUser' }, (response: Record<string, unknown>) => {
        void chrome.runtime.lastError;
        console.log('[Auth] SW user:', response?.user);
        void applyExtensionUserFromSw((response?.user as ExtensionUser | null) ?? null);
      });

      // Keep in sync with auth state changes broadcast by the service worker
      const listener = (message: Record<string, unknown>) => {
        if (message?.type === 'AUTH_STATE_CHANGED') {
          console.log('[Auth] SW AUTH_STATE_CHANGED:', message.user);
          void applyExtensionUserFromSw((message.user as ExtensionUser | null) ?? null);
        }
        if (message?.type === 'ACCOUNT_SWITCHED' && typeof message.aid === 'string') {
          if (shouldSkipAccountSwitchBroadcastReload()) {
            void refreshUserPreference();
            return;
          }
          void (async () => {
            try {
              await refreshUserPreference();
              useSessionsStore.getState().resetForAccountSwitch();
              await reloadSessionsAfterAccountSwitch();
            } catch (err) {
              console.warn('[Auth] ACCOUNT_SWITCHED handler failed', err);
            }
          })();
        }
        if (message?.type === 'ACTIVE_AID_CHANGED' && typeof message.aid === 'string') {
          setUser((prev) => (prev ? { ...prev, aid: message.aid as string } : prev));
        }
      };

      const authExpiredListener = () => {
        // A transient SW hiccup (MV3 cold start / token race) can surface as an
        // auth-expired event even though the session is still valid. Confirm
        // with the SW before tearing the UI down — only sign out when the SW
        // positively reports no user. Otherwise re-hydrate (or keep the
        // session) so the profile page doesn't bounce login -> home.
        void (async () => {
          const confirm = await getSwAuthUser();
          if (confirm.user) {
            void applyExtensionUserFromSwRef.current?.(confirm.user);
            return;
          }
          if (!confirm.ok) {
            // SW unreachable — do not force a logout on an unconfirmed signal.
            return;
          }
          try {
            chrome.runtime.sendMessage({ action: 'POPUP_SIGN_OUT' });
          } catch {
            // ignore
          }
          clearExtensionAuthUi();
          redirectExtensionPopupToLogin();
        })();
      };

      const emailNotVerifiedListener = () => {
        setUser(null);
        setLoading(false);
        saveExtensionReturnPath();
        redirectExtensionPopupToLogin();
      };

      window.addEventListener(AUTH_EXPIRED_EVENT, authExpiredListener);
      window.addEventListener(EMAIL_NOT_VERIFIED_EVENT, emailNotVerifiedListener);
      window.addEventListener(WORKSPACE_ACCESS_DENIED_EVENT, workspaceDeniedListener);
      chrome.runtime.onMessage.addListener(listener);
      return () => {
        window.removeEventListener(AUTH_EXPIRED_EVENT, authExpiredListener);
        window.removeEventListener(EMAIL_NOT_VERIFIED_EVENT, emailNotVerifiedListener);
        window.removeEventListener(WORKSPACE_ACCESS_DENIED_EVENT, workspaceDeniedListener);
        chrome.runtime.onMessage.removeListener(listener);
      };
    }

    const forceLogout = async () => {
      setUser(null);
      setLoading(false);
      try {
        await signOut(auth);
      } catch (error) {
        console.error('[Auth] Firebase sign-out failed after auth expiry:', error);
      }
    };

    // Normal web / Electron — use Firebase directly.
    console.log('[Auth] Listening for auth state changes...');

    if (isElectron()) {
      void getRedirectResult(auth)
        .then(async (result) => {
          if (result?.user) {
            await finalizeFirebaseSignIn(result.user);
          }
        })
        .catch((error) => {
          console.error('[Auth] Electron Google redirect result failed:', error);
        });
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[Auth] State changed:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user');

      if (firebaseUser) {
        try {
          const prefResponse = await api.get('/preference');
          const prefData = prefResponse.data as PreferenceApiPayload;
          throwIfPrefAppError(prefData as Record<string, unknown>);
          seedClientActiveAidFromPreference(prefData);
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            avatar: firebaseUser.photoURL || undefined,
            ...workspaceFieldsFromPreference(prefData),
          });
        } catch (error) {
          if (error instanceof PrefAppError) {
            if (isEmailNotVerifiedMessage(error.message)) {
              try {
                sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, error.message);
                sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
              } catch {
                // ignore
              }
              setUser(null);
              setLoading(false);
              return;
            }
            console.warn('[Auth] Preference app-level rejection in web context (keeping session):', error.message);
            setUser(userFromFirebaseWithFallback(firebaseUser));
            setLoading(false);
            return;
          }
          // API may reject with 403 and email-not-verified body — only when message explicitly says so (not "Unauthorized")
          const axiosData = (error as { response?: { data?: unknown } })?.response?.data;
          if (isEmailNotVerifiedResponse(axiosData)) {
            const msg = typeof (axiosData as { message?: string }).message === 'string'
              ? (axiosData as { message: string }).message
              : 'Email id not verified. Please check your email and verify your email address.';
            try {
              sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, msg);
              sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
            } catch {
              // ignore
            }
            setUser(null);
            setLoading(false);
            return;
          }
          if (isTransientAuthFailure(error)) {
            console.warn('[Auth] Transient token refresh failure loading preferences — keeping session');
            setUser(userFromFirebaseWithFallback(firebaseUser));
            setLoading(false);
            return;
          }
          if (isAxiosUnauthorizedError(error)) {
            const apiMessage =
              getMessageFromResponse(axiosData) || 'Session expired. Please sign in again.';
            try {
              sessionStorage.setItem(AUTH_ERROR_MESSAGE_KEY, apiMessage);
            } catch {
              // ignore
            }
            console.error('[Auth] Unauthorized loading preferences:', error);
            setUser(null);
            setLoading(false);
            await forceLogout();
            return;
          }
          console.warn('[Auth] Preference request failed (keeping session, using defaults):', error);
          setUser(userFromFirebaseWithFallback(firebaseUser));
          setLoading(false);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    const authExpiredListener = () => {
      void forceLogout();
    };

    const emailNotVerifiedListener = () => {
      setUser(null);
      setLoading(false);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, authExpiredListener);
    window.addEventListener(EMAIL_NOT_VERIFIED_EVENT, emailNotVerifiedListener);
    window.addEventListener(WORKSPACE_ACCESS_DENIED_EVENT, workspaceDeniedListener);

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, authExpiredListener);
      window.removeEventListener(EMAIL_NOT_VERIFIED_EVENT, emailNotVerifiedListener);
      window.removeEventListener(WORKSPACE_ACCESS_DENIED_EVENT, workspaceDeniedListener);
      unsubscribe();
    };
  }, [applyExtensionUserFromSw, refreshUserPreference]);

  // Extension app: keep chrome.storage.sync.highlighterEnabled aligned with API preference for content scripts.
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.protocol !== 'chrome-extension:') {
      return;
    }
    if (!user?.preference || user.preference.highlighter === undefined) {
      return;
    }
    const enabled = user.preference.highlighter !== false;
    try {
      const chromeApi = (
        window as unknown as {
          chrome?: { storage?: { sync?: { set: (o: object) => void } } };
        }
      ).chrome;
      chromeApi?.storage?.sync?.set({ highlighterEnabled: enabled });
    } catch {
      // ignore
    }
  }, [user?.id, user?.preference]);

  // Extension: floating page tools FAB visibility for content scripts.
  useEffect(() => {
    if (typeof window === 'undefined' || window.location.protocol !== 'chrome-extension:') {
      return;
    }
    if (!user?.preference) {
      return;
    }
    const fabOn = user.preference.pageToolsFab !== false;
    try {
      const chromeApi = (
        window as unknown as {
          chrome?: { storage?: { sync?: { set: (o: object) => void } } };
        }
      ).chrome;
      chromeApi?.storage?.sync?.set({ pageToolsFabEnabled: fabOn });
    } catch {
      // ignore
    }
  }, [user?.id, user?.preference]);

  const loginWithGoogle = async () => {
    const isExtension =
      typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
    setLoading(true);
    try {
      if (isExtension) {
        try {
          const swUser = await signInWithGoogleViaSw();
          await applyExtensionUserFromSw({
            uid: swUser.uid,
            email: swUser.email,
            displayName: swUser.displayName,
            photoURL: undefined,
          });
        } catch (swError) {
          if (isSwMfaRequiredError(swError)) {
            startMfaSignInFromSw(swError.mfaPending);
            throw new MfaRequiredError();
          }
          throw swError;
        }
        return;
      }
      console.log('[Auth] Starting Google Login...');
      if (isElectron()) {
        try {
          const result = await signInWithPopup(auth, googleProvider);
          await finalizeFirebaseSignIn(result.user);
          return;
        } catch (popupError) {
          if (isMultiFactorAuthRequired(popupError)) {
            startMfaSignIn(popupError, auth.currentUser?.email ?? '');
            throw new MfaRequiredError();
          }
          console.warn('[Auth] Google popup failed in Electron; falling back to redirect', popupError);
          await signInWithRedirect(auth, googleProvider);
          return;
        }
      }
      const result = await signInWithPopup(auth, googleProvider);
      await finalizeFirebaseSignIn(result.user);
    } catch (error) {
      if (error instanceof MfaRequiredError) {
        throw error;
      }
      if (isMultiFactorAuthRequired(error)) {
        startMfaSignIn(error, auth.currentUser?.email ?? '');
        throw new MfaRequiredError();
      }
      console.error('[Auth] Google Login Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithEmail = async (email: string, pass: string) => {
    const isExtension =
      typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
    setLoading(true);
    try {
      if (isExtension) {
        try {
          const swUser = await signInWithEmailAndPasswordViaSw(email, pass);
          await applyExtensionUserFromSw({
            uid: swUser.uid,
            email: swUser.email,
            displayName: swUser.displayName,
            photoURL: undefined,
          });
        } catch (swError) {
          if (isSwMfaRequiredError(swError)) {
            startMfaSignInFromSw(swError.mfaPending);
            throw new MfaRequiredError();
          }
          throw swError;
        }
        return;
      }
      console.log('[Auth] Starting Email Login...', email);
      const result = await signInWithEmailAndPassword(auth, email, pass);
      await finalizeFirebaseSignIn(result.user);
    } catch (error) {
      if (error instanceof MfaRequiredError) {
        throw error;
      }
      if (isSwMfaRequiredError(error)) {
        startMfaSignInFromSw(error.mfaPending);
        throw new MfaRequiredError();
      }
      if (isMultiFactorAuthRequired(error)) {
        startMfaSignIn(error, email);
        throw new MfaRequiredError();
      }
      console.error('[Auth] Email Login Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signupWithEmail = async (email: string, pass: string, name: string) => {
    const isExtension =
      typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
    setLoading(true);
    try {
      if (isExtension) {
        const swUser = await signUpViaSw(email, pass, name);
        await applyExtensionUserFromSw({
          uid: swUser.uid,
          email: swUser.email,
          displayName: swUser.displayName,
          photoURL: undefined,
        });
        return;
      }
      console.log('[Auth] Starting Email Signup...', email);
      const result = await createUserWithEmailAndPassword(auth, email, pass);
      const firebaseUser = result.user;
      const displayName = name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User';
      // Persist the chosen display name to Firebase so it survives reloads
      // (otherwise `onAuthStateChanged` falls back to the email local-part
      // and the dashboard greets the user with `name@host` instead of their
      // real name). Best-effort: a failure here doesn't break signup.
      if (name && firebaseUser.displayName !== name) {
        try {
          await updateProfile(firebaseUser, { displayName: name });
        } catch (err) {
          console.warn('[Auth] updateProfile(displayName) failed', err);
        }
      }
      try {
        const prefResponse = await api.get('/preference');
        const prefData = prefResponse.data as PreferenceApiPayload;
        throwIfPrefAppError(prefData);
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: displayName,
          avatar: firebaseUser.photoURL || undefined,
          ...workspaceFieldsFromPreference(prefData),
        });
      } catch (prefError) {
        if (prefError instanceof PrefAppError) {
          if (isEmailNotVerifiedMessage(prefError.message)) {
            try {
              sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, prefError.message);
              sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
            } catch {
              // ignore
            }
            setUser(null);
            throw prefError;
          }
          setUser({
            ...userFromFirebaseWithFallback(firebaseUser),
            name: displayName,
          });
          return;
        }
        const axiosData = (prefError as { response?: { data?: unknown } })?.response?.data;
        if (isEmailNotVerifiedResponse(axiosData)) {
          const msg =
            typeof (axiosData as { message?: string }).message === 'string'
              ? (axiosData as { message: string }).message
              : 'Email id not verified. Please check your email and verify your email address.';
          try {
            sessionStorage.setItem(EMAIL_VERIFICATION_MESSAGE_KEY, msg);
            sessionStorage.setItem(REDIRECT_VERIFY_EMAIL_KEY, '1');
          } catch {
            // ignore
          }
          setUser(null);
          throw prefError;
        }
        if (isTransientAuthFailure(prefError)) {
          setUser({
            ...userFromFirebaseWithFallback(firebaseUser),
            name: displayName,
          });
          return;
        }
        if (isAxiosUnauthorizedError(prefError)) {
          await signOut(auth);
          throw prefError;
        }
        setUser({
          ...userFromFirebaseWithFallback(firebaseUser),
          name: displayName,
        });
      }
    } catch (error) {
      console.error('[Auth] Email Signup Error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim(), buildPasswordResetActionCodeSettings());
  };

  const completeMfaSignIn = async (opts: {
    code: string;
    hintIndex?: number;
    phoneVerificationId?: string;
  }) => {
    const isExtension =
      typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
    if (isExtension && mfaViaServiceWorkerRef.current) {
      setLoading(true);
      try {
        const swUser = await completeMfaSignInViaSw(opts);
        clearMfaSignIn();
        await applyExtensionUserFromSw({
          uid: swUser.uid,
          email: swUser.email,
          displayName: swUser.displayName,
          photoURL: undefined,
        });
      } finally {
        setLoading(false);
      }
      return;
    }
    const resolver = mfaResolverRef.current;
    if (!resolver) {
      throw new Error('No MFA sign-in in progress.');
    }
    setLoading(true);
    try {
      const assertion = buildMfaAssertion(
        resolver,
        opts.hintIndex ?? 0,
        opts.code,
        opts.phoneVerificationId
      );
      const result = await resolver.resolveSignIn(assertion);
      clearMfaSignIn();
      await finalizeFirebaseSignIn(result.user);
    } finally {
      setLoading(false);
    }
  };

  const sendMfaPhoneVerification = async (hintIndex = 0): Promise<string> => {
    if (mfaViaServiceWorkerRef.current) {
      return sendMfaPhoneVerificationViaSw(hintIndex);
    }
    const resolver = mfaResolverRef.current;
    if (!resolver) {
      throw new Error('No MFA sign-in in progress.');
    }
    if (!mfaRecaptchaRef.current) {
      mfaRecaptchaRef.current = createInvisibleRecaptcha(auth, 'mfa-recaptcha-container');
    }
    return sendPhoneMfaVerificationCode(auth, resolver, hintIndex, mfaRecaptchaRef.current);
  };

  const logout = async () => {
    clearMfaSignIn();
    clearClientActiveAid();
    setLoading(true);
    try {
      const isExtension =
        typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
      if (isExtension) {
        // Service worker owns Firebase; page auth instance may not reflect the session.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const chromeApi = (window as any).chrome;
        await new Promise<void>((resolve, reject) => {
          try {
            chromeApi?.runtime?.sendMessage({ action: 'POPUP_SIGN_OUT', data: {} }, () => {
              void chromeApi?.runtime?.lastError;
              resolve();
            });
          } catch (e) {
            reject(e);
          }
        });
        setUser(null);
      } else {
        await signOut(auth);
        setUser(null);
      }
    } catch (error) {
      console.error('Logout Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (patch: Partial<UserPreferences>) => {
    const normalizedPatch: Partial<UserPreferences> = {
      ...patch,
      ...(patch.primaryColor !== undefined
        ? { primaryColor: normalizePrimaryColor(patch.primaryColor) }
        : {}),
    };

    const res = await api.put('/preference', normalizedPatch);
    const data = res.data as Record<string, unknown>;
    throwIfPrefAppError(data);
    if (data.status !== 'success') {
      throw new Error(typeof data.message === 'string' ? data.message : 'Could not save preferences.');
    }
    const pref = data.preference as UserPreferences | undefined;
    setUser(prev =>
      prev && pref
        ? { ...prev, preference: { ...prev.preference, ...pref } as UserPreferences }
        : prev
    );

    if (normalizedPatch.locale !== undefined) {
      useLocaleStore.getState().setLocale(normalizeLocale(normalizedPatch.locale));
    }

    if (normalizedPatch.primaryColor !== undefined) {
      useThemeStore.getState().setPrimaryColor(normalizePrimaryColor(normalizedPatch.primaryColor));
    }

    if (normalizedPatch.theme !== undefined) {
      useThemeStore.getState().setTheme(themeFromApiPreference(normalizedPatch.theme));
    }

    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'chrome-extension:' &&
      normalizedPatch.highlighter !== undefined
    ) {
      try {
        const chromeApi = (
          window as unknown as {
            chrome?: { storage?: { sync?: { set: (o: object) => void } } };
          }
        ).chrome;
        chromeApi?.storage?.sync?.set({ highlighterEnabled: normalizedPatch.highlighter });
      } catch (e) {
        console.warn('[Auth] Failed to sync highlighterEnabled to chrome.storage', e);
      }
    }

    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'chrome-extension:' &&
      normalizedPatch.pageToolsFab !== undefined
    ) {
      try {
        const chromeApi = (
          window as unknown as {
            chrome?: { storage?: { sync?: { set: (o: object) => void } } };
          }
        ).chrome;
        chromeApi?.storage?.sync?.set({ pageToolsFabEnabled: normalizedPatch.pageToolsFab });
      } catch (e) {
        console.warn('[Auth] Failed to sync pageToolsFabEnabled to chrome.storage', e);
      }
    }
  };

  const patchLocalPreference = useCallback((patch: Partial<UserPreferences>) => {
    setUser((prev) =>
      prev
        ? {
            ...prev,
            preference: { ...(prev.preference ?? FALLBACK_PREFERENCES), ...patch } as UserPreferences,
          }
        : prev
    );
  }, []);

  const updateDisplayName = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Display name is required.');
    }
    if (trimmed.length > 80) {
      throw new Error('Display name must be 80 characters or fewer.');
    }

    const isExtension =
      typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';

    if (isExtension) {
      const swUser = await updateProfileViaSw(trimmed);
      setUser((prev) =>
        prev ? { ...prev, name: swUser.displayName?.trim() || trimmed } : prev
      );
      return;
    }

    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('Not signed in.');
    }
    await updateProfile(firebaseUser, { displayName: trimmed });
    setUser((prev) => (prev ? { ...prev, name: trimmed } : prev));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        mfaPending,
        loginWithGoogle,
        loginWithEmail,
        signupWithEmail,
        requestPasswordReset,
        logout,
        cancelMfaSignIn: clearMfaSignIn,
        completeMfaSignIn,
        sendMfaPhoneVerification,
        updatePreference,
        patchLocalPreference,
        updateDisplayName,
        refreshUserPreference,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
